import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { FormApiService, IForm } from 'shared';

import { AppStateService } from './state';

@Injectable({ providedIn: 'root' })
export class FormService {
    private readonly _forms: BehaviorSubject<Array<IForm>> = new BehaviorSubject<Array<IForm>>([]);
    public readonly forms: Observable<Array<IForm>> = this._forms.asObservable();

    private readonly appState = inject(AppStateService);
    private readonly formApi = inject(FormApiService);

    public constructor() {
        this.appState.loggedInWithTenant.distinctStream
            .pipe(
                switchMap(async (loggedInWithTenant) => {
                    if (loggedInWithTenant) {
                        // Refresh data on login
                        await this.refreshForms();
                    } else {
                        // Clear data on logout
                        this._forms.next([]);
                    }
                })
            )
            .subscribe();
    }

    public async refreshForms(): Promise<Array<IForm>> {
        const forms = await firstValueFrom(this.formApi.getForms());
        this._forms.next(forms);
        return forms;
    }

    public async fetchForm(id: string): Promise<IForm> {
        const result = await firstValueFrom(this.formApi.getForm(id));

        // Update local cache with more detailed copy
        const forms = this._forms.value;
        const index = forms.findIndex((form) => form.id === id);
        if (index === -1) {
            forms.push(result);
        } else {
            forms[index] = result;
        }

        return result;
    }

    public getFormForIdSync(id: string): IForm | null {
        return this._forms.value.find((building) => building.id === id) ?? null;
    }
}
